import { useMemo } from 'react';
import { WarningType } from '@signalvote/shared';
import { useCurrentPeriodReport } from './useCurrentPeriodReport';
import { useFilingPreflight } from './useFinanceReports';

/**
 * The expenditure ids the federal disbursement-purpose preflight warning names, so the
 * close-hub ledger can mark the offending row itself instead of leaving the problem to be
 * discovered inside the review-and-file modal.
 *
 * Read straight off the preflight verdict — never re-derived here. The rule for "this purpose
 * says nothing" is the regulation's list of nine named-insufficient descriptions plus a
 * normalizer, and it lives in `FederalPurposeDescriptions`. A second copy
 * in TypeScript would drift silently the day either side changes, and the two surfaces would
 * then disagree about the same row — the ledger clean, the file modal blocking.
 *
 * Empty until a report exists for the open period (the preflight is report-scoped). That bounds
 * the mark to the close hub rather than the whole period, which is the trade for not owning a
 * second copy of the rules.
 */
export function useFederalFlaggedDisbursementPurposes(): ReadonlySet<string> {
  const current = useCurrentPeriodReport();
  const reportId = current.status === 'ready' ? current.data.reportId : null;
  const { data: preflight } = useFilingPreflight(reportId);

  return useMemo(
    () =>
      new Set(
        (preflight?.warnings ?? [])
          .filter((w) => w.warningType === WarningType.FederalDisbursementPurposeMissing)
          .flatMap((w) => w.affectedRowIds),
      ),
    [preflight],
  );
}
